import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { cn } from '@/v2/utils';
import { SupportModal } from '@/v2/components/SupportModal';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { canSeeRewards } from '@/utils/rewardsHelpers';
import {
  Home,
  FolderOpen,
  Layers,
  Plug,
  Workflow,
  LayoutGrid,
  Code2,
  PanelLeftClose,
  Crown,
  LifeBuoy,
  Gift,
  Users,
  type LucideIcon,
} from 'lucide-react';

const baseNavItems: { path: string; icon: LucideIcon; labelKey: string }[] = [
  { path: '/', icon: Home, labelKey: 'sidebar.navHome' },
  { path: '/projects', icon: FolderOpen, labelKey: 'sidebar.navProjects' },
  { path: '/assets', icon: Layers, labelKey: 'sidebar.navAssets' },
  // Templates — unified gallery: HeyGen visual STYLES (auto mode) + curated
  // video PRESETS (manual mode). Merged from the old /templates + /templates-v2.
  { path: '/templates', icon: LayoutGrid, labelKey: 'sidebar.navTemplates' },
  // No "Soon" badge here — users discover it's upcoming only when they open the
  // page (each shows a Coming-Soon overlay).
  { path: '/integrations', icon: Plug, labelKey: 'sidebar.navIntegrations' },
  { path: '/automations', icon: Workflow, labelKey: 'sidebar.navAutomations' },
  { path: '/developers', icon: Code2, labelKey: 'sidebar.navDevelopers' },
];

interface SidebarProps {
  desktopHidden: boolean;
  mobileOpen: boolean;
  onClose: () => void;
  onNavigate: () => void;
}

export function Sidebar({ desktopHidden, mobileOpen, onClose, onNavigate }: SidebarProps) {
  const { t } = useTranslation('v2');
  const [supportOpen, setSupportOpen] = useState(false);
  const user = useUserStore((s) => s.user);
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const inTeamWorkspace = !!activeOrgId;
  const showRewards = canSeeRewards(user, activeOrgId);
  const navItems = useMemo(() => {
    let items = showRewards
      ? [
          ...baseNavItems.slice(0, 4),
          { path: '/rewards', icon: Gift, labelKey: 'sidebar.navRewards' },
          ...baseNavItems.slice(4),
        ]
      : baseNavItems;
    // Team workspaces: the team hub (members / usage / billing) rides right
    // after Projects while a team workspace is active.
    if (inTeamWorkspace) {
      items = [
        ...items.slice(0, 2),
        { path: '/team', icon: Users, labelKey: 'sidebar.navTeam' },
        ...items.slice(2),
      ];
    }
    return items;
  }, [showRewards, inTeamWorkspace]);
  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen w-64 bg-surface/90 backdrop-blur-xl border-r border-glass-border flex flex-col z-40',
        'transition-transform duration-300',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        desktopHidden ? 'lg:-translate-x-full' : 'lg:translate-x-0'
      )}
    >
      {/* Logo */}
      <div className="p-6 flex items-center gap-3">
        <div className="relative">
          <img src="/pwa-512x512.png" alt="Vivideo" className="w-10 h-10 rounded-xl" />
          <div className="absolute inset-0 rounded-xl bg-primary/25 blur-md -z-10" />
        </div>
        <span className="text-xl font-bold text-text-primary">Vivideo</span>
        <button
          onClick={onClose}
          className="ml-auto w-9 h-9 rounded-lg flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-colors"
          aria-label={t('sidebar.hideSidebar')}
        >
          <PanelLeftClose className="w-[18px] h-[18px]" strokeWidth={1.75} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300',
                    'hover:bg-surface-light group',
                    isActive
                      ? 'bg-gradient-to-r from-primary/20 to-primary/10 text-primary border border-primary/30 shadow-[0_0_15px_rgba(139,92,246,0.15)]'
                      : 'text-text-secondary hover:text-text-primary border border-transparent'
                  )
                }
              >
                <item.icon className="w-5 h-5 shrink-0 transition-transform group-hover:scale-110" />
                <span className="font-medium text-sm leading-tight">{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}

          {/* Upgrade — the CTA: flaming gold crown, can't be missed.
              Hidden in a TEAM workspace (the team plan covers generation there;
              personal upgrades belong to the personal workspace). */}
          {!inTeamWorkspace && (
          <li className="pt-2">
            <NavLink
              to="/subscription"
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group overflow-hidden',
                  'border border-amber-400/40 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15',
                  'hover:border-amber-400/70 hover:shadow-[0_0_22px_rgba(245,158,11,0.35)] hover:-translate-y-0.5',
                  isActive && 'border-amber-400/70 shadow-[0_0_22px_rgba(245,158,11,0.3)]'
                )
              }
            >
              <span className="relative shrink-0">
                <Crown className="w-5 h-5 text-amber-400 drop-shadow-[0_0_6px_rgba(245,158,11,0.9)] transition-transform group-hover:scale-110" />
                <span className="absolute inset-0 blur-md bg-amber-400/50 -z-10 animate-pulse" />
              </span>
              <span className="font-semibold text-sm bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                {t('sidebar.upgrade')}
              </span>
              <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300">
                Pro
              </span>
            </NavLink>
          </li>
          )}
        </ul>
      </nav>

      {/* Support — opens the support window (mailto alone fails silently for
          users without a configured mail handler) */}
      <div className="p-4 border-t border-glass-border">
        <button
          onClick={() => setSupportOpen(true)}
          className="group w-full text-left flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-br from-primary/10 via-surface-light/40 to-accent/10 border border-primary/20 hover:border-primary/40 hover:shadow-glow-sm transition-all duration-300"
        >
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/25 to-accent/25 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
            <LifeBuoy className="w-5 h-5 text-primary" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-text-primary">{t('sidebar.needAHand')}</span>
            <span className="block text-[11px] text-text-tertiary group-hover:text-primary transition-colors">{t('sidebar.contactSupport')}</span>
          </span>
        </button>

      </div>

      {supportOpen && <SupportModal onClose={() => setSupportOpen(false)} />}
    </aside>
  );
}
